<template>
  <div class="obituary-threaded-comments__form create-comment-form">
    <div class="create-comment-form__container">
      <transition name="contact-form__reveal-error">
        <p
          class="form-errors"
          v-if="showErrors && formErrors"
          v-text="formErrors"
        />
      </transition>

      <div class="comment-input">
        <textarea
          rows="3"
          ref="textarea"
          v-model="comment"
          :class="commentHasErrors && 'has-errors'"
          :disabled="uploading"
          :placeholder="texts.threadedComments.writeAComment"
        />
      </div>

      <div class="create-comment-form__input-wrapper">
        <input
          type="text"
          v-model="name"
          :class="nameHasErrors && 'has-errors'"
          :disabled="uploading"
          :placeholder="`${texts.common.yourName}*`"
        >
      </div>
    </div>

    <div class="obituary-comments__submit-container">
      <button
        class="btn btn-cta"
        type="button"
        :class="disabled && 'disabled'"
        @click.prevent="submitAction"
        v-text="submitLabel"
      />

      <button
        class="btn link"
        type="button"
        @click.prevent="cancelAction"
        v-text="texts.common.cancel"
      />
    </div>
  </div>
</template>

<script>
  import { useScrollIntoView } from "Composables/useScrollIntoView";
  import { useIsMobile } from "Main/composables/useIsMobile";
  import Helpers from "Modules/Helpers";
  import { mapActions, mapMutations, mapState } from "vuex";

  export default {
    computed: {
      ...mapState("threadedComments", [
        "parent",
        "editComment",
        "editCommentHash",
        "modalContext",
      ]),

      commentHasErrors() {
        return this.showErrors && this.comment === "";
      },

      disabled() {
        if (this.uploading) {
          return true;
        }

        if (this.isEdit) {
          return (
            this.name === this.editComment.name &&
            this.comment === this.editComment.comment
          );
        }

        return this.name === "" || this.comment === "";
      },

      formErrors() {
        if (!this.disabled) {
          return null;
        }

        if (
          this.editComment &&
          this.name === this.editComment.name &&
          this.comment === this.editComment.comment
        ) {
          return this.texts.threadedComments.formValidationNoFieldHasChanged;
        }

        if (this.name === "" && this.comment === "") {
          return this.texts.threadedComments.formValidationNameMessageRequired;
        }

        if (this.name === "") {
          return this.texts.createComment.formValidationNameRequired;
        }

        return this.texts.threadedComments.formValidationMessageRequired;
      },

      isEdit() {
        return this.modalContext === "edit";
      },

      nameHasErrors() {
        return this.showErrors && this.name === "";
      },

      parentHasChildren() {
        return !!this.parent.children?.length;
      },

      parentId() {
        return this.parent.id;
      },

      submitLabel() {
        if (this.isEdit) {
          return this.texts.threadedComments.editComment;
        }

        return this.texts.threadedComments.addComment;
      },
    },

    data() {
      return {
        comment: "",
        loading: false,
        name: "",
        showErrors: false,
        texts: lavendlaObituary,
        uploading: false,
      };
    },

    methods: {
      ...mapMutations("threadedComments", [
        "setModalContext",
        "setEditComment",
        "setEditCommentHash",
      ]),
      ...mapActions({
        create: "comments/createObituaryComment",
        toggleModal: "threadedComments/toggleModal",
        update: "comments/updateObituaryComment",
      }),

      cancelAction() {
        if (this.parentHasChildren) {
          return this.setModalContext("list");
        }

        return this.toggleModal();
      },

      postComment() {
        if (!(this.name && this.comment)) {
          return;
        }

        this.uploading = true;

        const data = {
          comment: this.comment,
          id: "new",
          loading: true,
          name: this.name,
          obituaryId: this.obituaryId,
          parentId: this.parentId,
        };

        this.create(data)
          .then((comment) => {
            if (this.isMobile) {
              this.scrollCommentIntoView(comment);
            }

            Helpers.gtmObituaryEvent("threadedComment");
            this.setModalContext("list");
          })
          .catch((error) => {
            this.setModalContext("list");
            if (error.serverMessage) {
              // biome-ignore lint/suspicious/noAlert: no in-page error UI exists for this form yet
              alert(error.serverMessage);
            } else {
              // biome-ignore lint/suspicious/noAlert: no in-page error UI exists for this form yet
              alert(this.texts.threadedComments.createError);
            }
          });
      },

      reset() {
        this.uploading = false;
        this.loading = false;
        this.name = "";
        this.comment = "";
        this.showErrors = false;
        this.setEditComment();
        this.setEditCommentHash();
      },

      scrollCommentIntoView() {
        if (!this.parentId) {
          return;
        }

        this.scrollIntoViewCentered(
          ".single-obituary-comment__threaded-comments",
          `#comment-${this.parentId}`
        );
      },

      submitAction() {
        if (this.disabled) {
          return this.triggerShowErrors();
        }

        if (this.isEdit) {
          return this.updateComment();
        }

        return this.postComment();
      },

      triggerShowErrors() {
        this.showErrors = true;
      },

      updateComment() {
        if (this.disabled) {
          return;
        }

        const data = {
          hash: this.editCommentHash,
          id: this.editComment.id,
          parentId: this.parentId,
        };

        if (this.name !== this.editComment.name) {
          data.name = this.name;
        }

        if (this.comment !== this.editComment.comment) {
          data.comment = this.comment;
        }

        this.update(data)
          .then((comment) => {
            if (this.isMobile) {
              this.scrollCommentIntoView(comment);
            }

            this.setModalContext("list");
          })
          .catch((error) => {
            this.setModalContext("list");
            if (error.serverMessage) {
              // biome-ignore lint/suspicious/noAlert: no in-page error UI exists for this form yet
              alert(error.serverMessage);
            } else {
              // biome-ignore lint/suspicious/noAlert: no in-page error UI exists for this form yet
              alert(this.texts.threadedComments.updateError);
            }
          });
      },
    },

    mounted() {
      if (this.isEdit && this.editComment) {
        this.name = this.editComment.name;
        this.comment = this.editComment.comment;
      }
    },
    name: "ThreadedCommentsForm",

    props: {
      obituaryId: {
        required: true,
        type: String,
      },
    },

    setup() {
      const { isMobile, isTablet } = useIsMobile();
      const {
        scrollIntoView,
        scrollIntoViewCentered,
        scrollIntoViewAlignBottom,
      } = useScrollIntoView();
      return {
        isMobile,
        isTablet,
        scrollIntoView,
        scrollIntoViewAlignBottom,
        scrollIntoViewCentered,
      };
    },

    watch: {
      modalContext(context) {
        if (context === "create") {
          // Wait for the transition
          setTimeout(() => {
            this.$refs.textarea.focus();
          }, 500);
        } else if (context === "edit") {
          if (this.editComment) {
            this.name = this.editComment.name;
            this.comment = this.editComment.comment;
          }

          // Wait for the transition
          setTimeout(() => {
            this.$refs.textarea.focus();
          }, 500);
        } else {
          // Wait for the transition
          setTimeout(() => {
            this.reset();
          }, 400);
        }
      },
    },
  };
</script>
